// ==========================================================================
// components/EventSheet.tsx — add / edit an event (FF-020, quick add FF-062)
// A bottom sheet on the phone, a centred panel on desktop.
// FF-062 (10 Oct, James: "make it as easy as possible to make entries"):
//   opens straight from Home; one-tap picks for the day (Today, Tomorrow,
//   Saturday…) and the time (All day, 9am, 12pm, 3pm, 6pm); "Runs over a few
//   days" for bookings and trips (2 nights, 3 nights, a week, or a date);
//   repeat, where and notes tucked under "More". Only the title is required.
// ==========================================================================
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { addDays, format, nextSaturday, nextSunday, parseISO } from "date-fns";
import { CalendarDays, ChevronDown, Clock, MapPin, Moon, Repeat, Users, X } from "lucide-react";
import { Button, ErrorText } from "@/components/ui";
import { usePeople } from "@/lib/household";
import { REPEATS, mapsLink, tokenColour, useDeleteEvent, useSaveEvent, ymd, type EventDraft, type Occurrence } from "@/lib/calendar";

const input = "w-full rounded-md border border-line bg-bg px-3 py-2.5 text-ink outline-none focus:border-primary";
const TZ = "Australia/Brisbane";
const hm = (d: Date | null) => (d ? d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ }) : "");
const bneDate = (iso: string) => new Date(iso).toLocaleDateString("en-CA", { timeZone: TZ });
const plus1h = (t: string) => `${String(Math.min(23, Number(t.slice(0, 2)) + 1)).padStart(2, "0")}${t.slice(2)}`;
const nice = (t: string) => { const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`; };

/** A round pick button; filled when chosen. */
function Pick({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
      className={`shrink-0 rounded-full px-3.5 py-2 text-sm transition ${on ? "bg-primary font-semibold text-primary-ink shadow-sm" : "bg-surface-raised text-ink hover:bg-primary/10"}`}>
      {children}
    </button>
  );
}

function Row({ icon: Icon, children }: { icon: typeof Clock; children: ReactNode }) {
  return <div className="flex gap-3"><Icon className="mt-2 h-5 w-5 shrink-0 text-ink-muted" /><div className="min-w-0 flex-1 space-y-2">{children}</div></div>;
}

export default function EventSheet({ occ, date, time, onClose }: { occ?: Occurrence; date: string; time?: string; onClose: () => void }) {
  const people = usePeople(); const save = useSaveEvent(); const del = useDeleteEvent();
  const dialog = useRef<HTMLDivElement>(null);
  const close = useRef(onClose); close.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialog.current?.querySelector<HTMLInputElement>("input")?.focus();
    const keydown = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") { ev.preventDefault(); close.current(); }
      if (ev.key !== "Tab") return;
      const controls = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]') ?? []).filter((el) => el.getClientRects().length);
      const first = controls[0]; const last = controls.at(-1);
      if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last?.focus(); }
      else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); previous?.focus(); };
  }, []);
  const e = occ?.event;
  const [d, setD] = useState<EventDraft>(() => {
    const start = e ? (e.all_day ? e.start_date! : occ!.date) : date;
    const end = e ? (e.all_day ? e.end_date ?? "" : e.ends_at ? bneDate(e.ends_at) : "") : "";
    return {
      title: e?.title ?? "", notes: e?.notes ?? "", location: e?.location ?? "",
      all_day: e?.all_day ?? false,
      date: start,
      endDate: end > start ? end : "",
      startTime: e && !e.all_day ? hm(new Date(e.starts_at!)) : time ?? "09:00",
      endTime: e && !e.all_day && e.ends_at ? hm(new Date(e.ends_at)) : time ? plus1h(time) : "10:00",
      repeat: e?.repeat ?? "none", repeat_until: e?.repeat_until ?? "",
      people: e?.event_people.map((p) => p.person_id) ?? [],
    };
  });
  const set = <K extends keyof EventDraft>(k: K, v: EventDraft[K]) => setD((p) => ({ ...p, [k]: v }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [several, setSeveral] = useState(() => !!d.endDate);
  const [more, setMore] = useState(() => !!(e && (e.repeat !== "none" || e.location || e.notes)));
  const repeating = !!e && e.repeat !== "none";

  // Day picks: today, tomorrow, and the coming weekend.
  const today = new Date();
  const dayPicks = [
    { label: "Today", v: ymd(today) },
    { label: "Tomorrow", v: ymd(addDays(today, 1)) },
    { label: format(nextSaturday(today), "EEE d"), v: ymd(nextSaturday(today)) },
    { label: format(nextSunday(today), "EEE d"), v: ymd(nextSunday(today)) },
  ].filter((p, i, all) => all.findIndex((x) => x.v === p.v) === i);
  const timePicks = ["09:00", "12:00", "15:00", "18:00"];
  const nights = d.endDate ? Math.round((parseISO(d.endDate).getTime() - parseISO(d.date).getTime()) / 86400000) : 0;
  const setDate = (v: string) => setD((p) => {
    // Keep the length of a booking when its first day moves.
    const n = p.endDate ? Math.round((parseISO(p.endDate).getTime() - parseISO(p.date).getTime()) / 86400000) : 0;
    return { ...p, date: v, endDate: n > 0 ? ymd(addDays(parseISO(v), n)) : "" };
  });
  const setNights = (n: number) => set("endDate", n > 0 ? ymd(addDays(parseISO(d.date), n)) : "");
  const endsBeforeStart = !d.all_day && !d.endDate && d.endTime && d.endTime < d.startTime;
  const badRange = !!d.endDate && d.endDate <= d.date;

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (badRange) return;
    const base = several ? d : { ...d, endDate: "" };
    // Editing a repeating event moves the whole series; keep its first date.
    const draft = repeating ? { ...base, date: e!.all_day ? e!.start_date! : bneDate(e!.starts_at!) } : base;
    try { await save.mutateAsync({ id: e?.id, d: draft }); onClose(); }
    catch { /* Keep the draft open; the mutation error is rendered below. */ }
  }

  const summary = d.all_day
    ? `${format(parseISO(d.date), "EEE d MMM")}${several && d.endDate ? ` – ${format(parseISO(d.endDate), "EEE d MMM")}` : ""} · all day`
    : `${format(parseISO(d.date), "EEE d MMM")} ${nice(d.startTime)}${d.endTime ? ` – ${several && d.endDate ? `${format(parseISO(d.endDate), "EEE d MMM")} ` : ""}${nice(d.endTime)}` : ""}`;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/30 backdrop-blur-[2px] lg:items-center" onClick={onClose}>
      <div ref={dialog} role="dialog" aria-modal="true" aria-label={e ? "Edit event" : "Add an event"} className="cal-pop max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-surface shadow-soft lg:max-w-lg lg:rounded-2xl" onClick={(x) => x.stopPropagation()}>
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line lg:hidden" />
        <form onSubmit={submit} className="space-y-5 p-5 pt-3 lg:pt-5">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-hand text-2xl leading-none text-primary">{e ? "Edit event" : "Pop it in the calendar"}</div>
              <input required value={d.title} onChange={(x) => set("title", x.target.value)} placeholder="What's on?" aria-label="What's on?"
                className="mt-1 w-full border-0 border-b-2 border-line bg-transparent px-0 py-2 text-xl font-semibold text-ink outline-none placeholder:font-normal placeholder:text-ink-muted/70 focus:border-primary" />
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="-mr-1 rounded-full p-1.5 text-ink-muted hover:bg-surface-raised"><X className="h-5 w-5" /></button>
          </div>

          <Row icon={CalendarDays}>
            {!repeating && (
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5">
                {dayPicks.map((p) => <Pick key={p.label} on={d.date === p.v} onClick={() => setDate(p.v)}>{p.label}</Pick>)}
              </div>
            )}
            <input type="date" required value={d.date} disabled={repeating} onChange={(x) => x.target.value && setDate(x.target.value)} className={input} aria-label="Date" />
          </Row>

          <Row icon={Clock}>
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5">
              <Pick on={d.all_day} onClick={() => set("all_day", true)}>All day</Pick>
              {timePicks.map((t) => <Pick key={t} on={!d.all_day && d.startTime === t} onClick={() => setD((p) => ({ ...p, all_day: false, startTime: t, endTime: plus1h(t) }))}>{nice(t)}</Pick>)}
            </div>
            {!d.all_day && (
              <div className="grid grid-cols-2 gap-3">
                <label className="block space-y-1"><span className="text-xs text-ink-muted">From</span>
                  <input type="time" required value={d.startTime} onChange={(x) => { const v = x.target.value; setD((p) => ({ ...p, startTime: v, endTime: !p.endTime || p.endTime <= v ? plus1h(v) : p.endTime })); }} className={input} /></label>
                <label className="block space-y-1"><span className="text-xs text-ink-muted">To</span>
                  <input type="time" value={d.endTime} onChange={(x) => set("endTime", x.target.value)} className={input} /></label>
              </div>
            )}
            {endsBeforeStart && <p className="text-xs text-warn">Ends before it starts. If it runs overnight, turn on “Runs over a few days”.</p>}
          </Row>

          <Row icon={Moon}>
            <label className="flex cursor-pointer items-center justify-between rounded-md bg-surface-raised px-3 py-2.5">
              <span><span className="font-medium">Runs over a few days</span><span className="block text-xs text-ink-muted">A booking, a trip, someone staying</span></span>
              <input type="checkbox" checked={several} onChange={(x) => { setSeveral(x.target.checked); if (x.target.checked && !d.endDate) setNights(2); }} className="h-6 w-6 accent-primary" />
            </label>
            {several && (
              <>
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-0.5">
                  {[[1, "1 night"], [2, "2 nights"], [3, "3 nights"], [7, "A week"]].map(([n, l]) => <Pick key={n} on={nights === n} onClick={() => setNights(n as number)}>{l}</Pick>)}
                </div>
                <label className="block space-y-1"><span className="text-xs text-ink-muted">Until</span>
                  <input type="date" value={d.endDate} min={ymd(addDays(parseISO(d.date), 1))} onChange={(x) => set("endDate", x.target.value)} className={input} /></label>
                {badRange && <p className="text-xs text-bad">The last day needs to be after the first.</p>}
              </>
            )}
          </Row>

          <Row icon={Users}>
            <div className="flex flex-wrap gap-2">
              {people.data?.map((p) => {
                const on = d.people.includes(p.id);
                return (
                  <button key={p.id} type="button" aria-pressed={on} onClick={() => set("people", on ? d.people.filter((x) => x !== p.id) : [...d.people, p.id])}
                    className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm transition ${on ? "font-semibold text-white shadow-sm" : "bg-surface-raised text-ink"}`}
                    style={on ? { background: tokenColour(p.colour) } : undefined}>
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: on ? "white" : tokenColour(p.colour) }} />{p.display_name}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-ink-muted">{d.people.length ? "Tap again to take someone off." : "Nobody picked = the whole family."}</p>
          </Row>

          <button type="button" onClick={() => setMore(!more)} className="flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-ink">
            <ChevronDown className={`h-4 w-4 transition ${more ? "rotate-180" : ""}`} /> {more ? "Less" : "Repeat, where, notes"}
          </button>
          {more && (
            <div className="space-y-4">
              <Row icon={Repeat}>
                <div className="grid grid-cols-2 gap-3">
                  <select value={d.repeat} onChange={(x) => set("repeat", x.target.value as EventDraft["repeat"])} className={input} aria-label="Repeat">
                    {REPEATS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                  {d.repeat !== "none" && <input type="date" value={d.repeat_until} onChange={(x) => set("repeat_until", x.target.value)} className={input} aria-label="Repeat until" title="Until (optional)" />}
                </div>
              </Row>
              <Row icon={MapPin}>
                <div className="flex gap-2">
                  <input value={d.location} onChange={(x) => set("location", x.target.value)} placeholder="Where (optional)" className={input} />
                  {d.location.trim() && <a href={mapsLink(d.location)} target="_blank" rel="noreferrer" className="flex shrink-0 items-center gap-1 rounded-md border border-line px-3 text-sm font-medium text-primary">Map</a>}
                </div>
                <textarea rows={2} value={d.notes} onChange={(x) => set("notes", x.target.value)} placeholder="Notes (optional)" className={input} />
              </Row>
              {repeating && <p className="text-xs text-ink-muted">Changes apply to every repeat.</p>}
            </div>
          )}

          <div className="sticky bottom-0 -mx-5 -mb-5 space-y-2 border-t border-line bg-surface/95 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur">
            <ErrorText>{save.error ? "Couldn't save. Check the times and try again." : null}</ErrorText>
            <p className="num text-center text-xs text-ink-muted">{summary}</p>
            <Button type="submit" disabled={save.isPending || badRange || !d.title.trim()}>{save.isPending ? "Saving…" : e ? "Save changes" : "Add to calendar"}</Button>
            {e && !confirmDelete && <Button type="button" variant="ghost" onClick={() => setConfirmDelete(true)} className="!text-bad">Delete</Button>}
            {e && confirmDelete && (
              <div className="space-y-2 rounded-md border border-bad/40 p-3">
                {repeating && <Button type="button" variant="ghost" onClick={async () => { await del.mutateAsync({ id: e.id, onlyDate: occ!.date }); onClose(); }}>Delete just this one</Button>}
                <Button type="button" onClick={async () => { await del.mutateAsync({ id: e.id }); onClose(); }} className="!bg-bad">{repeating ? "Delete every repeat" : "Delete event"}</Button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
